import { useState } from 'react';

const SHARE_URL = 'https://rockhoundauto.com/military-discount';
const SHARE_TEXT = `🪖 Fellow service members — Rockhound Auto offers a Dog Tag Discount: $300 off any vehicle, or donate it to charity. No DD-214 required. Check it out:`;

const options = [
  {
    icon: 'ri-money-dollar-circle-fill',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200',
    title: 'You get $300 off',
    desc: 'Your fellow service member saves $300 on their next vehicle.',
  },
  {
    icon: 'ri-gift-fill',
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
    title: 'You earn $300',
    desc: 'When they buy, you receive a $300 referral reward.',
  },
  {
    icon: 'ri-heart-fill',
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    title: 'Or donate $600',
    desc: 'Combine both rewards and donate the full $600 to a charity of your choice.',
  },
];

export default function ShareDiscountBanner() {
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(SHARE_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Dog Tag Discount — Rockhound Auto',
        text: SHARE_TEXT,
        url: SHARE_URL,
      });
    } else {
      setShowModal(true);
    }
  };

  const shareLinks = [
    {
      label: 'Facebook',
      icon: 'ri-facebook-fill',
      color: 'bg-[#1877F2] hover:bg-[#1565d8]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(SHARE_TEXT)}`,
    },
    {
      label: 'X / Twitter',
      icon: 'ri-twitter-x-fill',
      color: 'bg-slate-900 hover:bg-slate-700',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(SHARE_URL)}`,
    },
    {
      label: 'SMS',
      icon: 'ri-message-2-fill',
      color: 'bg-emerald-600 hover:bg-emerald-700',
      href: `sms:?body=${encodeURIComponent(SHARE_TEXT + ' ' + SHARE_URL)}`,
    },
    {
      label: 'Email',
      icon: 'ri-mail-fill',
      color: 'bg-red-600 hover:bg-red-700',
      href: `mailto:?subject=${encodeURIComponent('Dog Tag Discount — Rockhound Auto')}&body=${encodeURIComponent(SHARE_TEXT + '\n\n' + SHARE_URL)}`,
    },
  ];

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-red-950 text-white relative overflow-hidden">
        {/* Subtle star pattern */}
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Ctext x='50%25' y='55%25' dominant-baseline='middle' text-anchor='middle' font-size='18' fill='white'%3E★%3C/text%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-share-forward-fill text-yellow-400 text-sm"></i>
              </div>
              <span className="text-sm font-semibold tracking-wide">Pass It On</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Share This Discount with Fellow Service Members
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Know a veteran or active duty service member who needs a vehicle? Share the Dog Tag Discount — and everyone wins.
            </p>
          </div>

          {/* Three options */}
          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {options.map((opt) => (
              <div
                key={opt.title}
                className={`rounded-xl border p-6 ${opt.bg} text-slate-900`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-white shadow-sm mb-4`}>
                  <i className={`${opt.icon} text-2xl ${opt.color}`}></i>
                </div>
                <h3 className="text-lg font-bold mb-2">{opt.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{opt.desc}</p>
              </div>
            ))}
          </div>

          {/* Callout strip */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-yellow-400/20 rounded-full">
                <i className="ri-information-line text-yellow-400 text-lg"></i>
              </div>
              <p className="text-sm text-slate-300">
                <strong className="text-white">How it works:</strong> They buy a vehicle using your referral → they save $300 → you earn $300 → or combine for a $600 charity donation.
              </p>
            </div>
            <a
              href="/referral"
              className="text-sm font-semibold text-yellow-400 hover:text-yellow-300 whitespace-nowrap underline underline-offset-2 cursor-pointer"
            >
              See full referral details →
            </a>
          </div>

          {/* Share buttons */}
          <div className="text-center">
            <p className="text-sm text-slate-400 uppercase tracking-widest font-semibold mb-5">
              Share via
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {shareLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target={s.label !== 'SMS' && s.label !== 'Email' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 ${s.color} text-white px-5 py-3 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${s.icon} text-sm`}></i>
                  </div>
                  {s.label}
                </a>
              ))}

              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/20 text-white px-5 py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${copied ? 'ri-check-line text-emerald-400' : 'ri-links-line'} text-sm`}></i>
                </div>
                {copied ? 'Copied!' : 'Copy Link'}
              </button>

              <button
                onClick={handleNativeShare}
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-share-forward-fill text-sm"></i>
                </div>
                Share Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Fallback share modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-share-forward-fill text-red-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Share the Dog Tag Discount</h3>
              <p className="text-sm text-slate-500">Choose how you'd like to share:</p>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {shareLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target={s.label !== 'SMS' && s.label !== 'Email' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  onClick={() => setShowModal(false)}
                  className={`inline-flex items-center justify-center gap-2 ${s.color} text-white px-4 py-3 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${s.icon} text-sm`}></i>
                  </div>
                  {s.label}
                </a>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
              <span className="text-xs text-slate-500 truncate flex-1">{SHARE_URL}</span>
              <button
                onClick={handleCopy}
                className="text-xs font-semibold text-red-600 hover:text-red-700 whitespace-nowrap cursor-pointer"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
